import React, { useMemo, useState } from 'react';
import { MobileLayout } from '../../components/MobileLayout';
import { MapPin, CalendarClock, Users, Layers, Navigation, TrendingUp, List, Map as MapIcon, Home, ChevronLeft, ChevronRight, ClipboardList, Clock, X } from 'lucide-react';
import { ProposalWizard, ProposalOpportunity } from '../../components/ProposalWizard';
import { CmZoneMap } from '../../components/CmZoneMap';
import { useScreenInit } from '../../useScreenInit.js';
// The three kinds of opportunity surfaced to the Cleaner Manager:
//  1. aggregazione: pulizie raggruppabili NON vicine a un tuo job
//  2. vicina:       una o più pulizie vicine a un tuo job esistente
//  3. combinazione: combinazioni di pulizie vicine a tuoi job esistenti
type SuggestionCategory = 'aggregazione' | 'vicina' | 'combinazione';
const CATEGORY_META: Record<SuggestionCategory, {
  label: string;
  dot: string;
  pill: string;
  icon: string;
  iconBg: string;
}> = {
  aggregazione: {
    label: 'Aggregazioni',
    dot: 'bg-coral',
    pill: 'bg-coral/5 text-coral border-coral/20',
    icon: 'text-coral',
    iconBg: 'bg-coral/10'
  },
  vicina: {
    label: 'Vicino a job',
    dot: 'bg-navy',
    pill: 'bg-navy/5 text-navy border-navy/20',
    icon: 'text-navy',
    iconBg: 'bg-navy/10'
  },
  combinazione: {
    label: 'Combinazioni',
    dot: 'bg-teal-deep',
    pill: 'bg-teal-deep/5 text-teal-deep border-teal-deep/20',
    icon: 'text-teal-deep',
    iconBg: 'bg-teal-deep/10'
  }
};
const CATEGORY_ORDER: SuggestionCategory[] = ['aggregazione', 'vicina', 'combinazione'];
// Opportunity tied to a specific day + its suggestion category
type DatedOpportunity = ProposalOpportunity & {
  dateKey: string;
  category: SuggestionCategory;
};
const ZONES = ['Milano · Navigli', 'Milano · Isola', 'Milano · Centro', 'Milano · Porta Romana', 'Milano · Tortona'];
// Generate a varied mix of opportunities across (almost) every day of a month —
// most days carry at least one of the three categories.
function buildMonthOpportunities(year: number, monthIndex: number): DatedOpportunity[] {
  const monthShort = MONTH_NAMES_SHORT[monthIndex];
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
  const out: DatedOpportunity[] = [];
  for (let day = 1; day <= daysInMonth; day++) {
    // ~one quiet day per week to feel natural
    if (day % 7 === 0) continue;
    const cats: SuggestionCategory[] = [];
    if (day % 2 === 1) cats.push('aggregazione');
    if (day % 3 === 0) cats.push('vicina');
    if (day % 5 === 0) cats.push('combinazione');
    if (cats.length === 0) cats.push('vicina');
    const date = new Date(year, monthIndex, day);
    const dayName = DAY_NAMES_SHORT[date.getDay()];
    const dateKey = `${day}-${monthShort.toLowerCase()}`;
    cats.forEach((category, idx) => {
      const type: ProposalOpportunity['type'] = category === 'vicina' ? 'singola' : 'gruppo';
      const jobsCount = category === 'aggregazione' ? 3 + day % 4 : category === 'combinazione' ? 2 + day % 2 : 1;
      out.push({
        id: `${dateKey}-${idx}`,
        title: category === 'vicina' ? 'Pulizia vicino a un tuo job' : category === 'combinazione' ? 'Combinazione vicino a tuoi job' : `Gruppo · ${jobsCount} pulizie ravvicinate`,
        zone: ZONES[(day + idx) % ZONES.length],
        type,
        jobsCount,
        window: category === 'vicina' ? `${dayName} ${day} ${monthShort}, 10:00` : `${dayName} ${day} ${monthShort}`,
        distanceKm: category === 'vicina' ? 0.4 : category === 'combinazione' ? 0.6 : 1.2,
        suggestedUnitPrice: category === 'vicina' ? 50 : category === 'combinazione' ? 47 : 45,
        dateKey,
        category
      });
    });
  }
  return out;
}
interface DayCategoryCounts {
  total: number;
  aggregazione: number;
  vicina: number;
  combinazione: number;
}
const EMPTY_COUNTS: DayCategoryCounts = {
  total: 0,
  aggregazione: 0,
  vicina: 0,
  combinazione: 0
};
function countByDay(ops: DatedOpportunity[]): Record<string, DayCategoryCounts> {
  return ops.reduce((acc, o) => {
    const day = acc[o.dateKey] ??= {
      total: 0,
      aggregazione: 0,
      vicina: 0,
      combinazione: 0
    };
    day.total += 1;
    day[o.category] += 1;
    return acc;
  }, {} as Record<string, DayCategoryCounts>);
}
// Booked interventi (incarichi) the cleaner already has, per day
interface Incarico {
  id: string;
  address: string;
  zone: string;
  time: string;
  type: string;
  client: string;
  status: 'Confermato' | 'In attesa';
  price: number;
}
const INCARICHI_BY_DAY: Record<string, Incarico[]> = {
  '24-gen': [{
    id: 'inc-24-1',
    address: 'Via Vigevano 18',
    zone: 'Milano · Navigli',
    time: '09:30',
    type: 'Pulizia turnover',
    client: 'Marco R.',
    status: 'Confermato',
    price: 45
  }, {
    id: 'inc-24-2',
    address: 'Ripa di Porta Ticinese 47',
    zone: 'Milano · Navigli',
    time: '13:00',
    type: 'Deep cleaning',
    client: 'Sophie L.',
    status: 'Confermato',
    price: 80
  }],
  '25-gen': [{
    id: 'inc-25-1',
    address: 'Via Tortona 12',
    zone: 'Milano · Tortona',
    time: '11:00',
    type: 'Pulizia turnover',
    client: 'Giovanni P.',
    status: 'In attesa',
    price: 45
  }],
  '27-gen': [{
    id: 'inc-27-1',
    address: 'Via Borsieri 5',
    zone: 'Milano · Isola',
    time: '08:30',
    type: 'Pulizia turnover',
    client: 'Anna K.',
    status: 'Confermato',
    price: 42
  }, {
    id: 'inc-27-2',
    address: 'Piazza Gae Aulenti 3',
    zone: 'Milano · Isola',
    time: '12:00',
    type: 'Pulizia turnover',
    client: 'Thomas W.',
    status: 'Confermato',
    price: 44
  }, {
    id: 'inc-27-3',
    address: 'Via Confalonieri 9',
    zone: 'Milano · Isola',
    time: '16:00',
    type: 'Deep cleaning',
    client: 'Elena M.',
    status: 'In attesa',
    price: 80
  }]
};
type FilterType = 'tutte' | SuggestionCategory;
type ViewMode = 'lista' | 'mappa';
interface DayOption {
  key: string;
  dayName: string;
  dayNum: string;
  month: string;
  suggestions: number;
  cats: DayCategoryCounts;
}
// Italian short labels
const DAY_NAMES_SHORT = ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'];
const MONTH_NAMES_SHORT = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];
const MONTH_NAMES_FULL = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];
// Build the full list of days for a given month/year, with suggestion counts
function buildMonthDays(year: number, monthIndex: number, counts: Record<string, DayCategoryCounts>): DayOption[] {
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
  const monthShort = MONTH_NAMES_SHORT[monthIndex];
  return Array.from({
    length: daysInMonth
  }, (_, i) => {
    const dayNum = i + 1;
    const date = new Date(year, monthIndex, dayNum);
    const key = `${dayNum}-${monthShort.toLowerCase()}`;
    const cats = counts[key] ?? EMPTY_COUNTS;
    return {
      key,
      dayName: DAY_NAMES_SHORT[date.getDay()],
      dayNum: String(dayNum),
      month: monthShort,
      suggestions: cats.total,
      cats
    };
  });
}
export function CmOpportunitaPage() {
  const screenInit = useScreenInit() as {
    filter?: FilterType;
    view?: ViewMode;
    date?: string;
  };
  // Coerce any legacy filter values (gruppo/singola) to the new categories
  const initialFilter: FilterType = ((): FilterType => {
    const f = screenInit?.filter as string | undefined;
    if (f === 'gruppo') return 'aggregazione';
    if (f === 'singola') return 'vicina';
    if (f === 'tutte' || f === 'aggregazione' || f === 'vicina' || f === 'combinazione') return f;
    return 'tutte';
  })();
  const [filter, setFilter] = useState<FilterType>(initialFilter);
  const [viewMode, setViewMode] = useState<ViewMode>(screenInit?.view ?? 'lista');
  const [selectedDateKey, setSelectedDateKey] = useState<string>(screenInit?.date ?? '24-gen');
  // Current month shown in the date selector (defaults to January 2024)
  const [monthDate, setMonthDate] = useState(new Date(2024, 0, 1));
  // Generate the month's opportunities + per-day counts + day cells
  const {
    monthOpportunities,
    monthDays
  } = useMemo(() => {
    const ops = buildMonthOpportunities(monthDate.getFullYear(), monthDate.getMonth());
    const counts = countByDay(ops);
    const days = buildMonthDays(monthDate.getFullYear(), monthDate.getMonth(), counts);
    return {
      monthOpportunities: ops,
      monthDays: days
    };
  }, [monthDate]);
  const goToPrevMonth = () => setMonthDate(d => new Date(d.getFullYear(), d.getMonth() - 1, 1));
  const goToNextMonth = () => setMonthDate(d => new Date(d.getFullYear(), d.getMonth() + 1, 1));
  const selectedDate = monthDays.find(d => d.key === selectedDateKey) ?? monthDays.find(d => d.suggestions > 0) ?? monthDays[0];
  const [activeOpp, setActiveOpp] = useState<ProposalOpportunity | null>(null);
  const [activeJob, setActiveJob] = useState<DatedOpportunity | null>(null);
  // Booked interventi for the selected day
  const dayIncarichi = INCARICHI_BY_DAY[selectedDateKey] ?? [];
  // Suggestions follow the selected day (and the category filter)
  const filtered = monthOpportunities.filter(o => o.dateKey === selectedDate.key && (filter === 'tutte' ? true : o.category === filter));
  const dateLabel = `${selectedDate.dayName} ${selectedDate.dayNum} ${selectedDate.month}`;
  return <MobileLayout data-id="element-1991">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-6" data-id="element-1992">
        <div data-id="element-1993">
          <h1 className="text-xl lg:text-2xl font-bold text-navy" data-id="element-1994">
            Opportunità di zona
          </h1>
        </div>
        {/* List / Map switch — icon only, white bg with border */}
        <div className="flex items-center bg-white border border-gray-200 rounded-lg p-1 shadow-sm flex-shrink-0" data-id="element-1995">
          <button onClick={() => setViewMode('lista')} aria-label="Vista lista" aria-pressed={viewMode === 'lista'} title="Vista lista" className={`flex items-center justify-center w-9 h-9 rounded-md transition-colors ${viewMode === 'lista' ? 'bg-coral text-white' : 'text-gray-500 hover:text-navy'}`} data-id="element-1996">
            <List size={18} data-id="element-1997" />
          </button>
          <button onClick={() => setViewMode('mappa')} aria-label="Vista mappa" aria-pressed={viewMode === 'mappa'} title="Vista mappa" className={`flex items-center justify-center w-9 h-9 rounded-md transition-colors ${viewMode === 'mappa' ? 'bg-coral text-white' : 'text-gray-500 hover:text-navy'}`} data-id="element-1998">
            <MapIcon size={18} data-id="element-1999" />
          </button>
        </div>
      </div>

      {/* Date selector (both views) */}
      <div className="mb-6" data-id="element-2000">
        {/* Month navigation */}
        <div className="flex items-center justify-between mb-3" data-id="element-2001">
          <button onClick={goToPrevMonth} aria-label="Mese precedente" className="flex items-center justify-center w-8 h-8 rounded-lg border border-gray-200 bg-white text-gray-500 hover:text-navy hover:border-gray-300 transition-colors" data-id="element-2002">
            <ChevronLeft size={16} data-id="element-2003" />
          </button>
          <span className="text-sm font-semibold text-navy" data-id="element-2004">
            {MONTH_NAMES_FULL[monthDate.getMonth()]} {monthDate.getFullYear()}
          </span>
          <button onClick={goToNextMonth} aria-label="Mese successivo" className="flex items-center justify-center w-8 h-8 rounded-lg border border-gray-200 bg-white text-gray-500 hover:text-navy hover:border-gray-300 transition-colors" data-id="element-2005">
            <ChevronRight size={16} data-id="element-2006" />
          </button>
        </div>

        {/* pt-2 gives the absolute badge room so it isn't clipped by the scroll container */}
        <div className="flex gap-2 overflow-x-auto pt-2 pb-1 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]" data-id="element-2007">
          {monthDays.map(d => {
          const active = d.key === selectedDateKey;
          return <button key={d.key} onClick={() => setSelectedDateKey(d.key)} className={`relative flex-shrink-0 w-16 rounded-xl border-2 py-2 flex flex-col items-center transition-all ${active ? 'border-coral bg-coral/5' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-2008">
                <span className={`text-[10px] uppercase tracking-wider font-medium ${active ? 'text-coral' : 'text-gray-500'}`} data-id="element-2009">
                  {d.dayName}
                </span>
                <span className={`text-lg font-bold leading-tight ${active ? 'text-navy' : 'text-gray-700'}`} data-id="element-2010">
                  {d.dayNum}
                </span>
                <span className="text-[10px] text-muted-foreground" data-id="element-2011">
                  {d.month}
                </span>
                {/* Badge = number of SUGGESTIONS that day — grey for 1, coral for 2+ */}
                {d.suggestions > 0 && <span className={`absolute -top-1.5 -right-1.5 min-w-4 h-4 px-1 rounded-full text-white text-[9px] font-bold flex items-center justify-center ${d.suggestions >= 2 ? 'bg-coral' : 'bg-gray-400'}`} data-id="element-2012">
                    {d.suggestions}
                  </span>}
              </button>;
        })}
        </div>

        {/* Selected-day breakdown by type — tap a tag to filter */}
        {selectedDate.suggestions > 0 ? <div className="flex items-center flex-wrap gap-2 mt-3" data-id="element-2013">
            {CATEGORY_ORDER.filter(cat => selectedDate.cats[cat] > 0).map(cat => {
          const isActive = filter === cat;
          return <button key={cat} onClick={() => setFilter(isActive ? 'tutte' : cat)} className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border transition-colors ${isActive ? CATEGORY_META[cat].pill : 'bg-white text-gray-600 border-border hover:border-gray-300'}`} data-id="element-2014">
                    <span className={`w-2 h-2 rounded-full ${CATEGORY_META[cat].dot}`} data-id="element-2015" />
                    {selectedDate.cats[cat]} {CATEGORY_META[cat].label}
                  </button>;
        })}
          </div> :
      // Static legend = esempio dei tag delle tre tipologie
      <div className="flex items-center flex-wrap gap-2 mt-3" data-id="element-2016">
            {CATEGORY_ORDER.map(cat => <span key={cat} className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground" data-id="element-2017">
                <span className={`w-2 h-2 rounded-full ${CATEGORY_META[cat].dot}`} data-id="element-2018" />
                {CATEGORY_META[cat].label}
              </span>)}
          </div>}
      </div>

      {/* MAP VIEW */}
      {viewMode === 'mappa' && <CmZoneMap onSendProposal={setActiveOpp} dateKey={selectedDateKey} dateLabel={dateLabel} data-id="element-2019" />}

      {/* LIST VIEW */}
      {viewMode === 'lista' && <>
          {/* Filters — by suggestion category */}
          <div className="flex items-center gap-2 mb-4 overflow-x-auto pb-1 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]" data-id="element-2020">
            {([{
          id: 'tutte',
          label: 'Tutte'
        }, {
          id: 'aggregazione',
          label: 'Aggregazioni'
        }, {
          id: 'vicina',
          label: 'Vicino a job'
        }, {
          id: 'combinazione',
          label: 'Combinazioni'
        }] as {
          id: FilterType;
          label: string;
        }[]).map(f => <button key={f.id} onClick={() => setFilter(f.id)} className={`flex-shrink-0 px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${filter === f.id ? 'bg-coral text-white border-coral' : 'bg-white text-gray-600 border-border hover:border-gray-300'}`} data-id="element-2021">
                {f.label}
              </button>)}
          </div>

          {/* Opportunity list */}
          {filtered.length > 0 ? <div className="space-y-3" data-id="element-2022">
              {filtered.map(opp => opp.type === 'gruppo' ? <GroupOpportunityCard key={opp.id} opp={opp} onPropose={() => setActiveOpp(opp)} data-id="element-2023" /> : <SingleOpportunityCard key={opp.id} opp={opp} onPropose={() => setActiveOpp(opp)} onShowJob={() => setActiveJob(opp)} data-id="element-2024" />)}
            </div> : <div className="bg-white border border-border rounded-xl p-8 text-center" data-id="element-2025">
              <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mx-auto mb-3" data-id="element-2026">
                <MapPin size={20} className="text-muted-foreground" data-id="element-2027" />
              </div>
              <p className="text-sm font-medium text-foreground" data-id="element-2028">
                Nessuna opportunità il {selectedDate.dayNum}{' '}
                {selectedDate.month}
              </p>
              <p className="text-xs text-muted-foreground mt-1" data-id="element-2029">
                Prova a selezionare un altro giorno con check-out in zona.
              </p>
            </div>}
        </>}

      <ProposalWizard isOpen={activeOpp !== null} opportunity={activeOpp} onClose={() => setActiveOpp(null)} data-id="element-2030" />

      {/* Related-job detail modal (from a "Vicino a un tuo job" opportunity) */}
      {activeJob && <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setActiveJob(null)} data-id="element-2031">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-md flex flex-col" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Dettaglio job correlato" data-id="element-2032">
            {/* Modal header */}
            <div className="flex items-center justify-between p-4 border-b border-border" data-id="element-2033">
              <div className="flex items-center gap-3 min-w-0" data-id="element-2034">
                <span className="w-10 h-10 rounded-xl bg-navy flex items-center justify-center flex-shrink-0 shadow-sm" data-id="element-2035">
                  <Home size={18} className="text-white" data-id="element-2036" />
                </span>
                <div className="min-w-0" data-id="element-2037">
                  <h2 className="text-base font-bold text-navy truncate" data-id="element-2038">
                    Job correlato
                  </h2>
                  <p className="text-xs text-muted-foreground truncate" data-id="element-2039">
                    {activeJob.zone}
                  </p>
                </div>
              </div>
              <button onClick={() => setActiveJob(null)} aria-label="Chiudi" className="p-2 hover:bg-muted rounded-lg transition-colors flex-shrink-0" data-id="element-2040">
                <X size={20} className="text-muted-foreground" data-id="element-2041" />
              </button>
            </div>

            {/* Modal content */}
            <div className="p-4 space-y-3" data-id="element-2042">
              <div className="grid grid-cols-2 gap-3" data-id="element-2043">
                <div className="rounded-xl border border-border p-3" data-id="element-2044">
                  <span className="block text-[10px] uppercase tracking-wider font-semibold text-muted-foreground" data-id="element-2045">
                    Data
                  </span>
                  <span className="block text-sm font-semibold text-foreground mt-0.5" data-id="element-2046">
                    {(() => {
                  const {
                    dayName,
                    dayNum,
                    month
                  } = parseCardDate(activeJob.window);
                  return `${dayName} ${dayNum} ${month}`;
                })()}
                  </span>
                </div>
                <div className="rounded-xl border border-border p-3" data-id="element-2047">
                  <span className="block text-[10px] uppercase tracking-wider font-semibold text-muted-foreground" data-id="element-2048">
                    Orario
                  </span>
                  <span className="block text-sm font-semibold text-foreground mt-0.5" data-id="element-2049">
                    {activeJob.window.split(', ')[1] ?? '—'}
                  </span>
                </div>
                <div className="rounded-xl border border-border p-3" data-id="element-2050">
                  <span className="block text-[10px] uppercase tracking-wider font-semibold text-muted-foreground" data-id="element-2051">
                    Distanza
                  </span>
                  <span className="inline-flex items-center gap-1 text-sm font-semibold text-navy mt-0.5" data-id="element-2052">
                    <Navigation size={13} data-id="element-2053" />a{' '}
                    {distanceLabel(activeJob.distanceKm)}
                  </span>
                </div>
                <div className="rounded-xl border border-border p-3" data-id="element-2054">
                  <span className="block text-[10px] uppercase tracking-wider font-semibold text-muted-foreground" data-id="element-2055">
                    Prezzo suggerito
                  </span>
                  <span className="block text-sm font-semibold text-foreground mt-0.5" data-id="element-2056">
                    €{activeJob.suggestedUnitPrice} / intervento
                  </span>
                </div>
              </div>

              <p className="text-xs text-muted-foreground flex items-start gap-1.5" data-id="element-2057">
                <MapPin size={13} className="flex-shrink-0 mt-0.5" data-id="element-2058" />
                <span data-id="element-2059">
                  Questa pulizia è vicina a un tuo job già confermato in zona{' '}
                  {activeJob.zone}: puoi proporla per ottimizzare gli
                  spostamenti.
                </span>
              </p>
            </div>

            {/* Modal footer */}
            <div className="flex items-center justify-end gap-3 p-4 border-t border-border" data-id="element-2060">
              <button onClick={() => setActiveJob(null)} className="px-4 h-10 rounded-xl text-sm font-semibold text-gray-600 hover:bg-muted transition-colors" data-id="element-2061">
                Chiudi
              </button>
              <button onClick={() => {
            setActiveOpp(activeJob);
            setActiveJob(null);
          }} className="px-4 h-10 bg-coral text-white rounded-xl text-sm font-semibold hover:bg-coral-600 transition-colors" data-id="element-2062">
                Invia proposta
              </button>
            </div>
          </div>
        </div>}
    </MobileLayout>;
}
// ---- Helpers ----
function distanceLabel(km: number) {
  return km < 1 ? `${Math.round(km * 1000)} m` : `${km} km`;
}
function efficiencyLabel(jobsCount: number) {
  return jobsCount >= 6 ? '+22%' : '+18%';
}
// ---- Shared footer ----
function CardFooter({
  price,
  onPropose
}: {
  price: number;
  onPropose: () => void;
}) {
  return <div className="flex items-center justify-between gap-3 pt-4 mt-4 border-t border-border" data-id="element-2063">
      <span className="text-xs text-muted-foreground" data-id="element-2064">
        Prezzo suggerito{' '}
        <span className="font-semibold text-foreground" data-id="element-2065">€{price}</span> a
        intervento
      </span>
      <button onClick={onPropose} className="px-4 h-10 bg-coral text-white rounded-xl text-sm font-semibold hover:bg-coral-600 transition-colors whitespace-nowrap" data-id="element-2066">
        Invia proposta
      </button>
    </div>;
}
// Parse a window string ("Mer 24 Gen" or "Mer 24 Gen, 10:00") into day parts
function parseCardDate(window: string): {
  dayName: string;
  dayNum: string;
  month: string;
} {
  const [datePart] = window.split(',');
  const parts = datePart.trim().split(/\s+/);
  return {
    dayName: parts[0] ?? '',
    dayNum: parts[1] ?? '',
    month: parts[2] ?? ''
  };
}
// Compact calendar-style day chip, mirroring the day cells in the strip above
function DayChip({
  window
}: {
  window: string;
}) {
  const {
    dayName,
    dayNum,
    month
  } = parseCardDate(window);
  return <div className="flex-shrink-0 w-12 rounded-lg border border-gray-200 bg-white py-1 flex flex-col items-center" data-id="element-2067">
      <span className="text-[9px] uppercase tracking-wider font-medium text-gray-500" data-id="element-2068">
        {dayName}
      </span>
      <span className="text-base font-bold leading-tight text-navy" data-id="element-2069">
        {dayNum}
      </span>
      <span className="text-[9px] text-muted-foreground" data-id="element-2070">{month}</span>
    </div>;
}
// Decorative mini-map thumbnail used as the card's leading visual
function MiniMap({
  accent = 'coral'
}: {
  accent?: 'coral' | 'teal';
}) {
  const pin = accent === 'teal' ? 'bg-teal-deep' : 'bg-coral';
  return <div className="w-12 h-12 lg:w-14 lg:h-14 rounded-xl overflow-hidden flex-shrink-0 relative bg-slate-100 border border-gray-200" data-id="element-2071">
      <svg viewBox="0 0 56 56" className="absolute inset-0 w-full h-full" aria-hidden="true" data-id="element-2072">
        <rect width="56" height="56" fill="#f1f5f9" data-id="element-2073" />
        <path d="M0 20 H56 M0 38 H56 M18 0 V56 M38 0 V56" stroke="#cbd5e1" strokeWidth="2" data-id="element-2074" />
        <rect x="4" y="4" width="10" height="12" fill="#e2e8f0" data-id="element-2075" />
        <rect x="42" y="42" width="10" height="10" fill="#e2e8f0" data-id="element-2076" />
      </svg>
      <span className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-4 h-4 rounded-full ${pin} border-2 border-white shadow-sm flex items-center justify-center`} data-id="element-2077">
        <MapPin size={9} className="text-white" data-id="element-2078" />
      </span>
    </div>;
}
// ---- GROUP card: aggregazione / combinazione ----
function GroupOpportunityCard({
  opp,
  onPropose
}: {
  opp: DatedOpportunity;
  onPropose: () => void;
}) {
  const maxDots = 5;
  const visible = Math.min(opp.jobsCount, maxDots);
  const extra = opp.jobsCount - visible;
  const isCombo = opp.category === 'combinazione';
  const meta = CATEGORY_META[opp.category];
  const [city, area] = opp.zone.split('·').map(s => s.trim());
  return <div className="bg-white border border-border rounded-xl p-4 lg:p-5" data-id="element-2079">
      {/* Header: mini-map + title/zone + category tag */}
      <div className="flex items-start justify-between gap-3 mb-4" data-id="element-2080">
        <div className="flex items-start gap-3 min-w-0" data-id="element-2081">
          <MiniMap accent={isCombo ? 'teal' : 'coral'} data-id="element-2082" />
          <div className="min-w-0" data-id="element-2083">
            <h3 className="text-sm font-semibold text-foreground" data-id="element-2084">
              {isCombo ? `Combinazione · ${opp.jobsCount} pulizie` : `Aggregazione · ${opp.jobsCount} pulizie`}
            </h3>
            {/* Zone — promoted to a prominent, readable line */}
            <div className="flex items-center gap-1.5 mt-1" data-id="element-2085">
              <MapPin size={14} className={`flex-shrink-0 ${meta.icon}`} data-id="element-2086" />
              <span className="text-sm font-semibold text-navy truncate" data-id="element-2087">
                {area ?? opp.zone}
              </span>
              {city && area && <span className="text-xs text-muted-foreground" data-id="element-2088">· {city}</span>}
            </div>
          </div>
        </div>
        <DayChip window={opp.window} data-id="element-2089" />
      </div>

      {/* Cluster visual: one marker per pulizia */}
      <div className="flex items-center gap-3 mb-4" data-id="element-2090">
        <div className="flex items-center -space-x-2" aria-hidden="true" data-id="element-2091">
          {Array.from({
          length: visible
        }).map((_, i) => <span key={i} className="w-7 h-7 rounded-full bg-coral border-2 border-white shadow-sm flex items-center justify-center" data-id="element-2092">
              <MapPin size={12} className="text-white" data-id="element-2093" />
            </span>)}
          {extra > 0 && <span className="w-7 h-7 rounded-full border-2 border-white shadow-sm flex items-center justify-center text-[10px] font-bold text-[#000000] bg-[#FFD6E6]" data-id="element-2094">
              +{extra}
            </span>}
        </div>
        <span className="text-xs text-muted-foreground" data-id="element-2095">
          {opp.jobsCount} pulizie raggruppabili
        </span>
      </div>

      <CardFooter price={opp.suggestedUnitPrice} onPropose={onPropose} data-id="element-2096" />
    </div>;
}
// ---- SINGLE card: proximity to an existing job ----
function SingleOpportunityCard({
  opp,
  onPropose,
  onShowJob
}: {
  opp: DatedOpportunity;
  onPropose: () => void;
  onShowJob: () => void;
}) {
  const [, timeStr] = opp.window.split(', ');
  const dist = distanceLabel(opp.distanceKm);
  const meta = CATEGORY_META.vicina;
  const [city, area] = opp.zone.split('·').map(s => s.trim());
  return <div className="bg-white border border-border rounded-xl p-4 lg:p-5" data-id="element-2097">
      {/* Header: mini-map + title/zone + date chip (parity with Aggregazione) */}
      <div className="flex items-start justify-between gap-3 mb-4" data-id="element-2098">
        <div className="flex items-start gap-3 min-w-0" data-id="element-2099">
          <MiniMap accent="coral" data-id="element-2100" />
          <div className="min-w-0" data-id="element-2101">
            <h3 className="text-sm font-semibold text-foreground" data-id="element-2102">
              Vicino a un tuo job
            </h3>
            {/* Zone — promoted to a prominent, readable line */}
            <div className="flex items-center gap-1.5 mt-1" data-id="element-2103">
              <MapPin size={14} className={`flex-shrink-0 ${meta.icon}`} data-id="element-2104" />
              <span className="text-sm font-semibold text-navy truncate" data-id="element-2105">
                {area ?? opp.zone}
              </span>
              {city && area && <span className="text-xs text-muted-foreground" data-id="element-2106">· {city}</span>}
            </div>
          </div>
        </div>
        <DayChip window={opp.window} data-id="element-2107" />
      </div>

      {/* Related job — clickable, opens the job detail modal */}
      <button type="button" onClick={onShowJob} aria-label="Vedi dettaglio del job correlato" className="w-full flex items-center gap-3 rounded-xl bg-navy/5 border border-navy/10 p-3 text-left hover:bg-navy/10 hover:border-navy/20 transition-colors" data-id="element-2108">
        <span className="w-10 h-10 rounded-xl bg-navy flex items-center justify-center flex-shrink-0 shadow-sm" data-id="element-2109">
          <Home size={18} className="text-white" data-id="element-2110" />
        </span>
        <div className="min-w-0 flex-1" data-id="element-2111">
          <span className="block text-[10px] uppercase tracking-wider font-semibold text-navy/60" data-id="element-2112">
            Il tuo job correlato
          </span>
          <span className="block text-sm font-semibold text-navy truncate" data-id="element-2113">
            {opp.zone}
            {timeStr ? ` · ${timeStr}` : ''}
          </span>
        </div>
        <span className="flex-shrink-0 inline-flex items-center gap-1 text-xs font-bold text-navy bg-white border border-navy/15 rounded-lg px-2.5 py-1.5" data-id="element-2114">
          <Navigation size={13} className="flex-shrink-0" data-id="element-2115" />a {dist}
        </span>
        <ChevronRight size={18} className="text-navy/40 flex-shrink-0" data-id="element-2116" />
      </button>

      <CardFooter price={opp.suggestedUnitPrice} onPropose={onPropose} data-id="element-2117" />
    </div>;
}